import { Component } from 'react'
import { appRef } from '../../app'
import { getString } from '../../locale'
import { HiCube, HiPlus, HiOutlineXCircle } from 'react-icons/hi'
import { toast } from 'react-toastify'
import {
    C6_COMPONENT_MODELS,
    getAllSoftwareVersions,
    ISoftwareVersion,
    SoftwareVersionComponent,
} from '../../api/0x0001/software-versions'
import { RouteComponentProps } from 'react-router-dom'
import { format } from 'date-fns'
import classNames from 'classnames'
import { ClientModel } from '../../api/clients'

type State = {
    softwareVersions?: ISoftwareVersion[]
    selectedModel: ClientModel
    selectedComponent: SoftwareVersionComponent
}

export class SoftwareManager extends Component<RouteComponentProps, State> {
    state: State = {
        softwareVersions: undefined,
        selectedModel: ClientModel.Standard,
        selectedComponent: 'app',
    }

    componentDidMount = async () => {
        appRef.setHeader(<HiCube className="me-2" size={28} />, () => getString('pagelabels.software-manager'))
        this.loadSoftwareVersions()
    }

    loadSoftwareVersions = async () => {
        try {
            const result = await getAllSoftwareVersions(this.state.selectedModel, this.state.selectedComponent)
            this.setState({ softwareVersions: result.data })
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    handleModelChange = (model: ClientModel) => {
        const isC6Capable = (C6_COMPONENT_MODELS as ClientModel[]).includes(model)
        const selectedComponent: SoftwareVersionComponent = isC6Capable ? this.state.selectedComponent : 'app'
        this.setState({ selectedModel: model, selectedComponent, softwareVersions: undefined }, () => {
            this.loadSoftwareVersions()
        })
    }

    handleComponentChange = (component: SoftwareVersionComponent) => {
        this.setState({ selectedComponent: component, softwareVersions: undefined }, () => {
            this.loadSoftwareVersions()
        })
    }

    handlePlusClick() {
        this.props.history.push(`/software-manager/new?model=${this.state.selectedModel}&component=${this.state.selectedComponent}`)
    }

    handleSoftwareVersionClick(softwareVersion: ISoftwareVersion) {
        this.props.history.push(`/software-manager/${softwareVersion.id}`)
    }

    render() {
        return (
            <div className="container flex flex-col items-center">
                {this.renderModelTabs()}
                {this.renderComponentTabs()}
                {this.renderSoftwareVersions()}
                {this.renderPlusButton()}
            </div>
        )
    }

    renderComponentTabs() {
        const { selectedModel, selectedComponent } = this.state
        const isC6Capable = (C6_COMPONENT_MODELS as ClientModel[]).includes(selectedModel)
        if (!isC6Capable) return undefined
        const components: { value: SoftwareVersionComponent; label: string }[] = [
            { value: 'app', label: getString('software_manager.component_app') },
            { value: 'c6', label: getString('software_manager.component_c6') },
        ]
        return (
            <div className="w-full px-10 mb-6">
                <div className="flex flex-wrap gap-2">
                    {components.map(component => (
                        <button
                            key={component.value}
                            onClick={() => this.handleComponentChange(component.value)}
                            className={classNames(
                                'px-4 py-2 rounded-lg font-medium transition-colors border',
                                selectedComponent === component.value
                                    ? 'bg-teal-600 text-white border-teal-600'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                            )}
                        >
                            {component.label}
                        </button>
                    ))}
                </div>
            </div>
        )
    }

    renderModelTabs() {
        const { selectedModel } = this.state
        const models = [
            { value: ClientModel.Standard, label: getString('client_model.standard') },
            { value: ClientModel.LTI525S, label: getString('client_model.lti525s') },
            { value: ClientModel.SYRUP_TOWER, label: getString('client_model.syrup_tower') },
            { value: ClientModel.FILTER_FILLER, label: getString('client_model.filter_filler') },
            { value: ClientModel.LTSP101, label: getString('client_model.ltsp101') },
            { value: ClientModel.LTSP70, label: getString('client_model.ltsp70') },
            { value: ClientModel.LTSP175, label: getString('client_model.ltsp175') },
            { value: ClientModel.LTCB220, label: getString('client_model.ltcb220') },
            { value: ClientModel.LTCB226, label: getString('client_model.ltcb226') },
        ]

        return (
            <div className="w-full px-10 mb-6">
                <div className="flex flex-wrap gap-2">
                    {models.map(model => (
                        <button
                            key={model.value}
                            onClick={() => this.handleModelChange(model.value)}
                            className={classNames(
                                'px-4 py-2 rounded-lg font-medium transition-colors',
                                selectedModel === model.value
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            )}
                        >
                            {model.label}
                        </button>
                    ))}
                </div>
            </div>
        )
    }

    renderPlusButton() {
        return (
            <HiPlus
                className="fixed bottom-10 md:end-10 bg-gray-50 dark:bg-gray-700 rounded-full shadow-md text-gray-500 cursor-pointer md:hover-grow"
                size={90}
                onClick={this.handlePlusClick.bind(this)}
            />
        )
    }

    renderSoftwareVersions() {
        const { softwareVersions } = this.state
        if (softwareVersions === undefined) return undefined
        if (softwareVersions.length === 0)
            return (
                <div className="w-full h-full dark:text-white flex flex-row items-center justify-center">
                    <HiOutlineXCircle className="me-1" />
                    <h1>{getString('software_manager.no_software_versions')}</h1>
                </div>
            )
        return <div className="w-full px-10">{softwareVersions.map(this.renderSoftwareVersion.bind(this))}</div>
    }

    renderSoftwareVersion(softwareVersion: ISoftwareVersion, i: number) {
        const modelLabel = this.getModelLabel(softwareVersion.model)
        return (
            <div
                key={i}
                className={classNames('w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden md:hover-grow', i !== 0 && 'mt-10')}
                onClick={() => this.handleSoftwareVersionClick(softwareVersion)}
            >
                <div className="md:flex md:items-center md:content-center md:ms-3">
                    <div className="md:flex-shrink-0">
                        <div className="text-8xl font-bold text-center object-cover md:w-24 dark:text-gray-400">{softwareVersion.id}</div>
                    </div>
                    <div className="p-4 md:py-6">
                        <div className="tracking-wide text-2xl text-indigo-500 font-semibold">{softwareVersion.version}</div>
                        {modelLabel && (
                            <div className="mt-1 text-gray-500 flex">
                                <div className="me-1">{getString('dashboard.model')}:</div>
                                <div>{modelLabel}</div>
                            </div>
                        )}
                        {softwareVersion.uploadedBy && softwareVersion.uploadedBy.username && (
                            <div className="mt-1 text-gray-500 flex">
                                <div className="me-1">{getString('software_manager.uploaded_by')}:</div>
                                <div className="ltr">{softwareVersion.uploadedBy.username}</div>
                            </div>
                        )}
                        {softwareVersion.createdAt && (
                            <div className="mt-2 text-sm dark:text-white">
                                {format(new Date(softwareVersion.createdAt), getString('software_manager.created_at_format'))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    getModelLabel(model: ClientModel): string {
        switch (model) {
            case ClientModel.Standard:
                return getString('client_model.standard')
            case ClientModel.LTI525S:
                return getString('client_model.lti525s')
            case ClientModel.SYRUP_TOWER:
                return getString('client_model.syrup_tower')
            case ClientModel.FILTER_FILLER:
                return getString('client_model.filter_filler')
            case ClientModel.LTSP101:
                return getString('client_model.ltsp101')
            case ClientModel.LTSP70:
                return getString('client_model.ltsp70')
            case ClientModel.LTSP175:
                return getString('client_model.ltsp175')
            case ClientModel.LTCB220:
                return getString('client_model.ltcb220')
            case ClientModel.LTCB226:
                return getString('client_model.ltcb226')
            default:
                return ''
        }
    }
}
